/**
 * Google Tag Manager (GTM) Tracking Utilities
 * Centralized management of GTM data attributes for consistent tracking across the application
 */

import { GTM_TRACKED_EVENTS, GtmEventKey, GtmEventName } from './gtm-events';

export { GTM_TRACKED_EVENTS } from './gtm-events';
export type { GtmEventKey } from './gtm-events';
export type { GtmEventName } from './gtm-events';

/**
 * GTM tracking attributes interface
 * data-gtm is required; other attributes are optional
 */
export interface GTMTrackingAttributes {
  /** GTM event name (required). Use GtmEventName. */
  'data-gtm': GtmEventName;
  /** Origin/source of the event */
  'data-origin'?: string;
  /** Target/action being performed */
  'data-target'?: string;
  /** Step in a workflow */
  'data-step'?: string;
  /** Status of the action */
  'data-status'?: string;
  /** Mode/type of operation */
  'data-mode'?: string;
}

/**
 * Converts GTM tracking attributes object to DOM data attributes
 * Filters out undefined/null/empty string values and returns only defined attributes
 *
 * @param attributes - GTM tracking attributes object
 * @returns Object with data attributes ready to spread into JSX elements
 *
 * @example
 * ```tsx
 * <Button {...getGTMAttributes({ 'data-gtm': 'send_interview', 'data-origin': 'action_bar' })} />
 * ```
 */
export const getGTMAttributes = (
  attributes: Partial<GTMTrackingAttributes> | GTMTrackingAttributes
): Record<string, string> => {
  const attrs: Record<string, string> = {};

  const keys: (keyof GTMTrackingAttributes)[] = [
    'data-gtm',
    'data-origin',
    'data-target',
    'data-step',
    'data-status',
    'data-mode',
  ];

  for (const key of keys) {
    const value = attributes[key];
    if (value) {
      attrs[key] = value;
    }
  }

  return attrs;
};

/**
 * Creates GTM tracking attributes. First param must be an event key (e.g. 'SECTION_VIEWED').
 *
 * @param gtm - Event key from GTM_TRACKED_EVENTS (required). Use 'REQUEST_DEMO', 'NAVIGATE_SECTION', etc. — not raw strings.
 * @param options - Optional origin, target, step, status, mode
 * @returns GTM tracking attributes object
 */
export const createGTMTracking = (
  gtm: GtmEventKey,
  options?: {
    /** Origin/source of the event → data-origin */
    origin?: string;
    /** Target/action being performed → data-target */
    target?: string;
    /** Step in a workflow → data-step */
    step?: string;
    /** Status of the action → data-status */
    status?: string;
    /** Mode/type of operation → data-mode */
    mode?: string;
  }
): GTMTrackingAttributes => {
  const attributes: GTMTrackingAttributes = {
    'data-gtm': GTM_TRACKED_EVENTS[gtm],
  };

  if (options?.origin) attributes['data-origin'] = options.origin;
  if (options?.target) attributes['data-target'] = options.target;
  if (options?.step) attributes['data-step'] = options.step;
  if (options?.status) attributes['data-status'] = options.status;
  if (options?.mode) attributes['data-mode'] = options.mode;

  return attributes;
};
